/**
 * O resumo das falhas de e-mail (`TP-037`): só contagem, para a fila de triagem
 * avisar que há e-mail parado sem carregar a lista de falhas.
 *
 * Mora em `shared/` porque quem lê é a fila de triagem e quem muda o dado é a tela
 * *Falhas de e-mail* — uma feature não importa a outra. A chave fica SOB a raiz
 * `falhas-de-email`: invalidar a raiz (o que a tela de falhas faz depois de uma
 * rodada) atualiza a faixa também.
 */

import { queryOptions } from '@tanstack/react-query';
import type { EmailFailuresSummaryResponse } from '@anery/api-contracts';

import { requisitar } from '../api/clienteHttp';

export const RAIZ_DAS_FALHAS_DE_EMAIL = ['falhas-de-email'] as const;

/** A partir de quantas tentativas a falha conta como esgotada. Espelha `ReprocessMailFailures.TentativasMaximas`. */
export const TENTATIVAS_QUE_ESGOTAM = 5;

export interface ResumoDasFalhasDeEmail {
  /** Todas as falhas abertas que a sessão vê. */
  readonly abertas: number;
  readonly coleta: number;
  readonly triagem: number;
  /** As abertas que já atingiram o teto de tentativas (parte de `abertas`, não soma). */
  readonly esgotadas: number;
  /** As falhas de triagem da caixa que a tela tria (as que o botão alcança). Parte de `triagem`. */
  readonly triaveis: number;
  /**
   * A caixa de automação não tem núcleo configurado: a triagem nem começa e nada vira falha, então a
   * contagem sozinha diria zero. Só a sessão transversal recebe `true`.
   */
  readonly caixaSemRota: boolean;
  /**
   * Quantas leituras de e-mail rodaram nas últimas 24 h. Sinal silencioso do parser/agente de IA — não
   * vira linha de `email_erro`, então não aparece em nenhum dos campos acima. Só preenchido para quem vê
   * a caixa de automação (mesmo escopo de `triaveis`); senão zero.
   */
  readonly leiturasUltimas24h: number;
  /** Das `leiturasUltimas24h`, quantas rodaram sem o agente de IA. */
  readonly leiturasSemAgenteUltimas24h: number;
  /** Quantos e-mails a leitura automática desistiu (teto de tentativas). */
  readonly leiturasDesistidas: number;
}

export function mapearResumoDasFalhas(bruto: EmailFailuresSummaryResponse): ResumoDasFalhasDeEmail {
  return {
    abertas: bruto.open,
    coleta: bruto.collection,
    triagem: bruto.triage,
    esgotadas: bruto.exhausted,
    triaveis: bruto.triable,
    caixaSemRota: bruto.mailboxWithoutRoute,
    leiturasUltimas24h: bruto.readingLast24h,
    leiturasSemAgenteUltimas24h: bruto.readingWithoutAgentLast24h,
    leiturasDesistidas: bruto.readingGaveUp,
  };
}

/**
 * Sem retry: se o resumo falha, a faixa diz que não conseguiu verificar — repetir em silêncio só
 * atrasaria essa resposta, e a fila de triagem não depende dele.
 */
export function opcoesDoResumoDasFalhasDeEmail() {
  return queryOptions({
    queryKey: [...RAIZ_DAS_FALHAS_DE_EMAIL, 'resumo'] as const,
    queryFn: ({ signal }): Promise<ResumoDasFalhasDeEmail> =>
      requisitar<EmailFailuresSummaryResponse>('/falhas-de-email/resumo', { sinal: signal }).then(
        mapearResumoDasFalhas,
      ),
    retry: false,
  });
}
